"use client";

import { c as _c } from "react/compiler-runtime";
import { useEffect, useRef } from "react";
import { useRouter } from "next/navigation";
import { Loader2, CheckCircle2, AlertTriangle, X, BookUp } from "lucide-react";
import { useAuth } from "@/providers/auth-provider";
import { useChapterImportStore } from "@/stores/chapter-import-store";
import type { ChapterImportJobView } from "@/lib/import/types";
const POLL_INTERVAL_MS = 2500;
function isActive(job: ChapterImportJobView): boolean {
  return job.status === "queued" || job.status === "running";
}
function progressLine(job: ChapterImportJobView): string {
  if (job.filesProcessed === 0) return "Uploading & converting…";
  return `Imported ${job.chaptersCreated} ${job.chaptersCreated === 1 ? "chapter" : "chapters"} (${job.filesProcessed}/${job.totalFiles} files)…`;
}
function summaryLine(job: ChapterImportJobView): string {
  const base = `Added ${job.chaptersCreated} ${job.chaptersCreated === 1 ? "chapter" : "chapters"}.`;
  return job.errors.length > 0 ? `${base} ${job.errors.length} file${job.errors.length === 1 ? "" : "s"} skipped.` : base;
}
export function ChapterImportTray() {
  const $ = _c(23);
  const {
    user
  } = useAuth();
  const router = useRouter();
  const jobs = useChapterImportStore(_temp);
  const refresh = useChapterImportStore(_temp2);
  const dismiss = useChapterImportStore(_temp3);
  let t0;
  let t1;
  if ($[0] !== refresh || $[1] !== user) {
    t0 = () => {
      if (!user) {
        return;
      }
      refresh();
    };
    t1 = [user, refresh];
    $[0] = refresh;
    $[1] = user;
    $[2] = t0;
    $[3] = t1;
  } else {
    t0 = $[2];
    t1 = $[3];
  }
  useEffect(t0, t1);
  let t2;
  if ($[4] !== jobs) {
    t2 = jobs.some(isActive);
    $[4] = jobs;
    $[5] = t2;
  } else {
    t2 = $[5];
  }
  const hasActive = t2;
  let t3;
  let t4;
  if ($[6] !== hasActive || $[7] !== refresh || $[8] !== user) {
    t3 = () => {
      if (!user || !hasActive) {
        return;
      }
      const id = setInterval(refresh, POLL_INTERVAL_MS);
      return () => clearInterval(id);
    };
    t4 = [user, hasActive, refresh];
    $[6] = hasActive;
    $[7] = refresh;
    $[8] = user;
    $[9] = t3;
    $[10] = t4;
  } else {
    t3 = $[9];
    t4 = $[10];
  }
  useEffect(t3, t4);
  let t5;
  if ($[11] === Symbol.for("react.memo_cache_sentinel")) {
    t5 = new Set();
    $[11] = t5;
  } else {
    t5 = $[11];
  }
  const handledCompletions = useRef(t5);
  let t6;
  let t7;
  if ($[12] !== jobs || $[13] !== router) {
    t6 = () => {
      for (const job of jobs) {
        if (job.status !== "succeeded") {
          continue;
        }
        if (handledCompletions.current.has(job.id)) {
          continue;
        }
        handledCompletions.current.add(job.id);
        router.refresh();
      }
    };
    t7 = [jobs, router];
    $[12] = jobs;
    $[13] = router;
    $[14] = t6;
    $[15] = t7;
  } else {
    t6 = $[14];
    t7 = $[15];
  }
  useEffect(t6, t7);
  if (!user || jobs.length === 0) {
    return null;
  }
  let t8;
  if ($[16] !== dismiss || $[17] !== jobs) {
    let t9;
    if ($[19] !== dismiss) {
      t9 = job_0 => <div key={job_0.id} className="rounded-xl border border-border bg-card/95 p-3 shadow-lg backdrop-blur-md"><div className="flex items-start gap-2.5"><div className="mt-0.5 shrink-0">{job_0.status === "succeeded" ? <CheckCircle2 className="h-4 w-4 text-primary" aria-hidden="true" /> : job_0.status === "failed" ? <AlertTriangle className="h-4 w-4 text-destructive" aria-hidden="true" /> : <Loader2 className="h-4 w-4 animate-spin text-primary" aria-hidden="true" />}</div><div className="min-w-0 flex-1"><p className="flex items-center gap-1.5 text-sm font-medium text-foreground"><BookUp className="h-3.5 w-3.5 text-primary" aria-hidden="true" />{job_0.status === "succeeded" ? "Chapters imported" : job_0.status === "failed" ? "Import failed" : "Importing chapters"}</p><p className="mt-0.5 text-xs text-muted-foreground">{job_0.status === "succeeded" ? summaryLine(job_0) : job_0.status === "failed" ? job_0.error ?? "Something went wrong. Please try again." : progressLine(job_0)}</p></div>{!isActive(job_0) && <button onClick={() => dismiss(job_0.id)} aria-label="Dismiss" className="shrink-0 rounded-md p-1 text-muted-foreground transition-colors hover:bg-muted hover:text-foreground"><X className="h-3.5 w-3.5" aria-hidden="true" /></button>}</div></div>;
      $[19] = dismiss;
      $[20] = t9;
    } else {
      t9 = $[20];
    }
    t8 = jobs.map(t9);
    $[16] = dismiss;
    $[17] = jobs;
    $[18] = t8;
  } else {
    t8 = $[18];
  }
  let t9;
  if ($[21] !== t8) {
    t9 = <>{t8}</>;
    $[21] = t8;
    $[22] = t9;
  } else {
    t9 = $[22];
  }
  return t9;
}
function _temp3(s_1) {
  return s_1.dismiss;
}
function _temp2(s_0) {
  return s_0.refresh;
}
function _temp(s) {
  return s.jobs;
}